/* eslint-disable no-console */
import {InfoCircleOutlined} from '@ant-design/icons';
import {Col, Row, Form, Select} from 'antd';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import React, {useEffect, useState} from 'react';
import {useSearchParams} from 'react-router-dom';

import {ReactComponent as YearCalender} from '../../assets/yearCalender.svg';
import {CommonErrors, TF} from '../../constants/Common';
import {EfdbName} from '../../constants/efdbNameConstants';
import InformativeText from '../../constants/InformativeText';
import {sortBy} from '../../shared/commonFunctions';
import NumberInput from '../../shared/NumberInput';
import useCompanyStore from '../../store/companyStore/companyDetailsStore';
import useJSONStore from '../../store/StaticJson/jsonStore';
import RegEx from '../../utils/FormUtils/RegEx';
import {yearSelect} from '../../utils/FormUtils/yearSelect';
import Loader from '../Loader/Loader';
import {MYConfig, MYData} from '../MonthlyYearly/Interface/common';
import MonthlyYearlyDrawerForm from '../MonthlyYearly/MonthlyYearlyDrawerForm';

import {yearKeyLabel} from './Constants/IP/commonKeyLabel';
import {DceKey, DceLabel} from './Constants/Products/DceKeyLabel';
import {unitTypes} from './interface/unitChange';
import changeUnit from './Utils/changeUnit';
import {filterSelectOptions} from './Utils/commonFunctions';

const DceForm: React.FC<{
  rawMaterialFilter: any;
  data: any;
  currentStep?: number;
  setFormSelectedYear: React.Dispatch<React.SetStateAction<string>>;
  setIsFormLoading: React.Dispatch<React.SetStateAction<boolean>>;
  isFormLoading: boolean;
}> = ({
  rawMaterialFilter,
  data,
  currentStep,
  setFormSelectedYear,
  setIsFormLoading,
  isFormLoading
}) => {
  const FormInstance = useFormInstance();
  const [searchParam] = useSearchParams();
  const {getFieldValue, setFields} = FormInstance;
  const dceInfo = InformativeText.Product.sold_products;
  const refridgentUsed = useJSONStore.getState().refridgentUsed;
  const deviceFuel = useJSONStore.getState().deviceFuel;
  const {electricity} = useCompanyStore.getState().preUnits;

  const yearFilter = yearSelect();

  const [fuelState, setFuelState] = useState<any>(unitTypes.WEIGHT);
  const [refState, setRefState] = useState<any>(unitTypes.WEIGHT);

  useEffect(() => {
    setFuelState(
      deviceFuel?.find((item) => {
        return (
          item?.value ===
          (getFieldValue(DceKey.FUEL_TYPE) ?? data?.[DceKey.FUEL_TYPE])
        );
      })
    );
  }, [getFieldValue(DceKey.FUEL_TYPE)]);

  useEffect(() => {
    setRefState(
      refridgentUsed?.find((item) => {
        return (
          item?.value ===
          (getFieldValue(DceKey.REFRIGERATOR_TYPE) ??
            data?.[DceKey.REFRIGERATOR_TYPE])
        );
      })
    );
  }, [getFieldValue(DceKey.REFRIGERATOR_TYPE)]);

  const {productFilter} = rawMaterialFilter;

  const monYear: MYConfig[] = [
    {
      mainTitle: {
        key: DceKey.UNIT_SOLD_TF,
        title: DceLabel.UNIT_SOLD_TF
      },
      unit: 'No.'
    }
  ];

  return (
    <>
      {isFormLoading ? (
        <Loader />
      ) : (
        <>
          {currentStep === 0 && (
            <>
              <Col className="gutter-row">
                <Form.Item
                  name={yearKeyLabel.key}
                  label={yearKeyLabel.label}
                  labelCol={{span: 24}}
                  initialValue={searchParam.get('selectedYear')}
                >
                  <Select
                    placeholder={yearKeyLabel.label}
                    options={yearFilter}
                    suffixIcon={<YearCalender />}
                    disabled={data?._id !== undefined}
                    onChange={(e) => {
                      setFormSelectedYear(e);
                      setIsFormLoading(true);
                      if (getFieldValue(DceKey.PRODUCT_ID)) {
                        setFields([
                          {
                            name: DceKey.PRODUCT_ID,
                            errors: ['Please select facility again'],
                            value: undefined
                          }
                        ]);
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row">
                <Form.Item
                  name={DceKey.PRODUCT_ID}
                  rules={[
                    {
                      required: true,
                      message: CommonErrors.mandatory(DceLabel.PRODUCT_ID)
                    }
                  ]}
                  label={DceLabel.PRODUCT_ID}
                  labelCol={{span: 24}}
                >
                  <Select
                    disabled={data !== undefined}
                    showSearch
                    filterOption={filterSelectOptions}
                    className="select"
                    placeholder={DceLabel.PRODUCT_ID}
                    options={sortBy(productFilter, 'label', 'asc')}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row">
                <Form.Item
                  name={DceKey.LIFE_TIME}
                  rules={[
                    {
                      pattern: RegEx.nonDecimal,
                      message: CommonErrors.nonDecimalNonNegative
                    }
                  ]}
                  tooltip={{
                    icon: <InfoCircleOutlined />,
                    title: dceInfo.totalLife
                  }}
                  label={DceLabel.LIFE_TIME}
                  labelCol={{span: 24}}
                >
                  <NumberInput placeholder={DceLabel.LIFE_TIME} />
                </Form.Item>
              </Col>
            </>
          )}
          {currentStep === 1 && (
            <>
              <MonthlyYearlyDrawerForm
                config={monYear}
                data={getFieldValue(TF) as MYData[]}
              />
              <Row gutter={[16, 8]}>
                <Col className="gutter-row" span={12}>
                  <Form.Item
                    name={DceKey.FUEL_TYPE}
                    rules={[]}
                    label={DceLabel.FUEL_TYPE}
                    labelCol={{span: 24}}
                  >
                    <Select
                      showSearch
                      filterOption={filterSelectOptions}
                      className="select"
                      placeholder={DceLabel.FUEL_TYPE}
                      options={sortBy(
                        deviceFuel.filter((item) => {
                          return item?.dbName === EfdbName.ghg_global;
                        }),
                        'label',
                        'asc'
                      )}
                      onChange={(_, option) => {
                        setFuelState(option);
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col className="gutter-row" span={12}>
                  <Form.Item
                    name={DceKey.FUEL_USED}
                    rules={[
                      {
                        pattern: RegEx.nonDecimal,
                        message: CommonErrors.nonDecimalNonNegative
                      }
                    ]}
                    label={DceLabel.FUEL_USED}
                    labelCol={{span: 24}}
                  >
                    <NumberInput
                      placeholder={DceLabel.FUEL_USED}
                      addonAfter={changeUnit(fuelState?.state)}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[16, 8]}>
                <Col className="gutter-row" span={12}>
                  <Form.Item
                    name={DceKey.REFRIGERATOR_TYPE}
                    rules={[]}
                    label={DceLabel.REFRIGERATOR_TYPE}
                    labelCol={{span: 24}}
                  >
                    <Select
                      showSearch
                      filterOption={filterSelectOptions}
                      className="select"
                      placeholder={DceLabel.REFRIGERATOR_TYPE}
                      options={sortBy(
                        refridgentUsed.filter((item) => {
                          return item?.dbName === EfdbName.ghg_global;
                        }),
                        'label',
                        'asc'
                      )}
                      onChange={(_, option) => {
                        setRefState(option);
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col className="gutter-row" span={12}>
                  <Form.Item
                    name={DceKey.REFRIGERATOR_USED}
                    rules={[
                      {
                        pattern: RegEx.nonDecimal,
                        message: CommonErrors.nonDecimalNonNegative
                      }
                    ]}
                    label={DceLabel.REFRIGERATOR_USED}
                    labelCol={{span: 24}}
                  >
                    <NumberInput
                      placeholder={DceLabel.REFRIGERATOR_USED}
                      addonAfter={changeUnit(refState?.state)}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Col className="gutter-row" span={8}>
                <Form.Item
                  name={DceKey.ELEC_CONSUME}
                  rules={[
                    {
                      pattern: RegEx.nonDecimal,
                      message: CommonErrors.nonDecimalNonNegative
                    }
                  ]}
                  label={DceLabel.ELEC_CONSUME}
                  labelCol={{span: 24}}
                >
                  <NumberInput
                    placeholder={DceLabel.ELEC_CONSUME}
                    addonAfter={electricity}
                  />
                </Form.Item>
              </Col>
            </>
          )}
        </>
      )}
    </>
  );
};

export default DceForm;
